import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

/**
 * Calculates the difference in time between two Date objects and dispatches actions to update the counts
 * for seconds, minutes, hours, and days.
 *
 * This function calculates the difference in time between two Date objects and dispatches
 * actions to update the counts for seconds, minutes, hours, and days.
 *
 * @param {Function} dispatch - The dispatch function from useReducer to update state
 * @param {Date} prevTime - The previous timestamp
 * @param {Date} currentTime - The current timestamp
 */
function calculateAndUpdateCounts(dispatch, prevTime, currentTime) {
  // Calculate the difference in seconds between the two times
  const elapsedSeconds = Math.floor((currentTime - prevTime) / 1000);

  // Calcuate how many full days, hours, minutes, and seconds have passed
  const daysPassed = Math.floor(elapsedSeconds / (60 * 60 * 24));
  const hoursPassed = Math.floor((elapsedSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutesPassed = Math.floor((elapsedSeconds % (60 * 60)) / 60);
  const secondsPassed = elapsedSeconds % 60;

  // Dispatch the actions to update all the counts
  if (daysPassed > 0) {
    dispatch({ type: 'INCREMENT_DAY', payload: daysPassed });
  }

  if (hoursPassed > 0) {
    dispatch({ type: 'INCREMENT_HOUR', payload: hoursPassed });
  }

  if (minutesPassed > 0) {
    dispatch({ type: 'INCREMENT_MINUTE', payload: minutesPassed });
  }

  if (secondsPassed > 0) {
    dispatch({ type: 'INCREMENT_SECOND', payload: secondsPassed });
  }
}

const TimeWatcherContext = createContext();

export const useTimeWatcherContext = () => useContext(TimeWatcherContext);

const initialState = {
  curTime: new Date(),
  secondCount: 0,
  minuteCount: 0,
  hourCount: 0,
  dayCount: 0,
  running: true,
  pauseTime: null, // Keep track of the time when the timer was paused
};

function timeWatcherReducer(state, action) {
  switch (action.type) {
    case 'SET_CUR_TIME':
      return { ...state, curTime: action.payload };
    case 'INCREMENT_SECOND':
      return { ...state, secondCount: state.secondCount + 1 };
    case 'INCREMENT_MINUTE':
      return { ...state, minuteCount: state.minuteCount + 1 };
    case 'INCREMENT_HOUR':
      return { ...state, hourCount: state.hourCount + 1 };
    case 'INCREMENT_DAY':
      return { ...state, dayCount: state.dayCount + 1 };
    case 'START_TIMER':
      return { ...state, running: true, pauseTime: null };
    case 'PAUSE_TIMER':
      return { ...state, running: false, pauseTime: new Date() };
    case 'TOGGLE_RUNNING':
      return {
        ...state,
        running: !state.running,
        pauseTime: state.running ? new Date() : null, // Set pause time when stopping
      };
    default:
      return state;
  }
}

export default function TimeWatcherProvider({ children }) {
  const [state, dispatch] = useReducer(timeWatcherReducer, initialState);
  const tasksEachSecond = useRef([]);
  const tasksEachMinute = useRef([]);
  const tasksEachHour = useRef([]);
  const tasksEachDay = useRef([]);

  const setCurTime = useCallback((time) => {
    dispatch({ type: 'SET_CUR_TIME', payload: time });
  }, []);

  const startTimer = useCallback(() => {
    if (state.running) return;
    if (state.pauseTime) {
      const currentTime = new Date();
      calculateAndUpdateCounts(dispatch, state.pauseTime, currentTime);
    }
    dispatch({ type: 'START_TIMER' });
  }, [state.running, state.pauseTime]);

  const pauseTimer = useCallback(() => {
    if (state.running) {
      dispatch({ type: 'PAUSE_TIMER' });
    }
  }, [state.running]);

  const toggleRunning = useCallback(() => {
    if (state.running) {
      // When stopping, we store the pause time
      dispatch({ type: 'TOGGLE_RUNNING' });
    } else {
      // When resuming, calculate the elapsed time and update the counts
      const currentTime = new Date();
      calculateAndUpdateCounts(dispatch, state.pauseTime, currentTime);
      dispatch({ type: 'TOGGLE_RUNNING' });
    }
  }, [state.running]);

  // Methods to add and remove tasks
  const addRunEachSecond = useCallback((func) => {
    if (!tasksEachSecond.current.includes(func)) {
      tasksEachSecond.current.push(func);
    }
  }, []);

  const removeRunEachSecond = useCallback((func) => {
    if (!tasksEachSecond.current.includes(func)) return;
    tasksEachSecond.current = tasksEachSecond.current.filter((f) => f !== func);
  }, []);

  const addRunEachMinute = useCallback((func) => {
    if (!tasksEachMinute.current.includes(func)) {
      tasksEachMinute.current.push(func);
    }
  }, []);

  const removeRunEachMinute = useCallback((func) => {
    if (!tasksEachMinute.current.includes(func)) return;
    tasksEachMinute.current = tasksEachMinute.current.filter((f) => f !== func);
  }, []);

  const addRunEachHour = useCallback((func) => {
    if (!tasksEachHour.current.includes(func)) {
      tasksEachHour.current.push(func);
    }
  }, []);

  const removeRunEachHour = useCallback((func) => {
    if (!tasksEachHour.current.includes(func)) return;
    tasksEachHour.current = tasksEachHour.current.filter((f) => f !== func);
  }, []);

  const addRunEachDay = useCallback((func) => {
    if (!tasksEachDay.current.includes(func)) {
      tasksEachDay.current.push(func);
    }
  }, []);

  const removeRunEachDay = useCallback((func) => {
    if (!tasksEachDay.current.includes(func)) return;
    tasksEachDay.current = tasksEachDay.current.filter((f) => f !== func);
  }, []);

  // Use InternalClock to manage the timer
  useInternalClock({
    running: state.running,
    dispatch,
    taskRefs: {
      second: tasksEachSecond,
      minute: tasksEachMinute,
      hour: tasksEachHour,
      day: tasksEachDay,
    },
  });

  const value = useMemo(() => {
    return {
      ...state,
      setCurTime,
      startTimer,
      pauseTimer,
      toggleRunning,
      addRunEachSecond,
      removeRunEachSecond,
      addRunEachMinute,
      removeRunEachMinute,
      addRunEachHour,
      removeRunEachHour,
      addRunEachDay,
      removeRunEachDay,
    };
  }, [state]);

  return (
    <TimeWatcherContext.Provider value={value}>
      {children}
    </TimeWatcherContext.Provider>
  );
}

TimeWatcherProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Manages the internal clock for time tracking and task execution
 *
 * @param {Object} options - Clock configuration options
 * @param {boolean} options.running - Whether the clock is currently running
 * @param {Function} options.dispatch - Reducer dispatch function
 * @param {Object} options.taskRefs - References to task collections
 * @returns {void} - This hook only has side effects
 */
export function useInternalClock({ running, dispatch, taskRefs }) {
  useEffect(() => {
    let prevTime = new Date();

    const intervalId = setInterval(() => {
      if (!running) return;

      const newTime = new Date();
      dispatch({ type: 'SET_CUR_TIME', payload: newTime });

      // Check for day change
      if (newTime.getDay() !== prevTime.getDay()) {
        dispatch({ type: 'INCREMENT_DAY' });
        taskRefs.day.current.forEach((task) => {
          try {
            task();
          } catch (error) {
            console.error('Error executing day task:', error);
          }
        });
      }

      // Check for hour change
      if (newTime.getHours() !== prevTime.getHours()) {
        dispatch({ type: 'INCREMENT_HOUR' });
        taskRefs.hour.current.forEach((task) => {
          try {
            task();
          } catch (error) {
            console.error('Error executing hour task:', error);
          }
        });
      }

      // Check for minute change
      if (newTime.getMinutes() !== prevTime.getMinutes()) {
        dispatch({ type: 'INCREMENT_MINUTE' });
        taskRefs.minute.current.forEach((task) => {
          try {
            task();
          } catch (error) {
            console.error('Error executing minute task:', error);
          }
        });
      }

      // Always increment seconds
      dispatch({ type: 'INCREMENT_SECOND' });
      taskRefs.second.current.forEach((task) => {
        try {
          task();
        } catch (error) {
          console.error('Error executing second task:', error);
        }
      });

      prevTime = newTime;
    }, 1000);

    // Clean up on unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [running, dispatch, taskRefs]);
}
